// ═══════════════════════════════════════════════════════════════════════
// TRANSPORTE · o que toda chamada à API atravessa
// ═══════════════════════════════════════════════════════════════════════
// Mudou de casa em 07/10/2026, quando a Rotina Contábil passou a ser
// interface própria. O código é o MESMO que vinha no topo do `App.js`: nada
// foi reescrito, só deslocado. As duas interfaces herdam a blindagem entre
// empresas e o token sem precisar saber que elas existem — duplicar isto
// seria duplicar a trava que impediu os R$ 680.000 de uma empresa
// aparecerem na tela de outra.

import { supabase } from '../lib/supabase';

// ═══════════════════════════════════════════════════════════════════════
// BLINDAGEM ANTI-CONTAMINACAO ENTRE EMPRESAS (30/07/2026)
// ═══════════════════════════════════════════════════════════════════════
// Defeito que motivou: com a MORED selecionada, o Dashboard exibiu
// R$ 680.000,00 — receita exata da CONSTRUMAX. Ao trocar de empresa ficavam
// duas requisicoes em voo e a mais lenta sobrescrevia a mais nova. Dos 28
// useEffect que buscam dados, apenas 8 tinham trava contra resposta obsoleta.
//
// A protecao vive no TRANSPORTE, nao em cada tela: toda chamada a API passa
// pelo interceptor abaixo. Se a URL carrega o id de uma empresa que nao e
// mais a selecionada — na saida OU na chegada da resposta — a chamada e
// descartada silenciosamente. Quem escrever a proxima tela herda a protecao
// sem precisar saber que ela existe.
//
// So filtra ids de empresa CONHECIDOS (registrados por `focarEmpresa`). Um
// id de consultoria, produto ou insumo na URL passa livre.
//
// NAO usar regex de UUID aqui: as empresas sinteticas tem id de texto
// (`construmax`, `shopmax`, `sabor_arte`, `clinicavida`) e escapariam do
// filtro — justamente o par Construmax/MORED que produziu o defeito.

// Registro global: quem esta em foco e quais ids sao de empresa.
if (typeof window !== 'undefined') {
  window.__empresaEmFoco = window.__empresaEmFoco || null;
  window.__idsDeEmpresa  = window.__idsDeEmpresa  || new Set();
}

// Requisicoes em voo, para abortar as da empresa anterior na troca.
const _emVoo = new Set();

// Chamado no corpo do AppPrivado a cada render — idempotente, so age na troca.
export const focarEmpresa = (idAtual, todosOsIds) => {
  if (typeof window === 'undefined') return;
  if (Array.isArray(todosOsIds)) {
    todosOsIds.forEach(id => id && window.__idsDeEmpresa.add(String(id).toLowerCase()));
  }
  const novo = idAtual ? String(idAtual).toLowerCase() : null;
  if (novo === window.__empresaEmFoco) return;
  window.__empresaEmFoco = novo;
  // Cancela o que ficou em voo para as OUTRAS empresas — devolve as conexoes
  // ao pool do navegador em vez de deixa-las presas ate o servidor responder.
  _emVoo.forEach(r => { if (r.alvo !== novo) { try { r.abortar(); } catch (e) {} } });
};

// Devolve o id de empresa presente na URL, ou null se nao houver.
// Compara segmento a segmento contra o registro — cobre id em UUID e em texto.
const empresaDaURL = (urlStr) => {
  const partes = String(urlStr).split(/[/?&=]/);
  for (const p of partes) {
    const id = p.toLowerCase();
    if (id && window.__idsDeEmpresa.has(id)) return id;
  }
  return null;
};

// Promise que nunca resolve: o .then nao roda, o setState nao acontece e o
// .catch nao dispara erro falso na tela. A resposta obsoleta simplesmente
// deixa de existir. O effect da empresa nova ja esta a caminho.
const DESCARTADA = () => new Promise(() => {});

// ── Token em memoria (30/07/2026) ────────────────────────────────────
// Antes, TODA chamada a API aguardava `supabase.auth.getSession()`. Numa troca
// de empresa saem 6 a 8 chamadas de uma vez, e cada uma parava no Supabase
// antes de tocar o backend — a tela ficava presa em "Carregando..." mesmo com
// o backend respondendo em 0,03s.
//
// Agora o token vive numa variavel, atualizada pelo `onAuthStateChange` (que o
// app ja escutava). O interceptor so le. A primeira chamada, antes de a sessao
// chegar, ainda aguarda o `getSession` — dai em diante e leitura de memoria.
let _tokenEmMemoria = null;
export const guardarToken = (token) => { _tokenEmMemoria = token || null; };

const obterToken = async () => {
  if (_tokenEmMemoria) return _tokenEmMemoria;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    _tokenEmMemoria = session?.access_token || null;
  } catch (e) {
    console.warn('Erro ao obter token do Supabase:', e);
  }
  return _tokenEmMemoria;
};

// Interceptor de fetch: instalado UMA UNICA VEZ na carga do modulo.
// Anexa automaticamente o token JWT do Supabase em todas as chamadas a API.
// Roda antes de qualquer componente, evitando race conditions.
if (typeof window !== 'undefined' && !window.__fetchInterceptorInstalled) {
  const fetchOriginal = window.fetch;
  window.fetch = async (url, options = {}) => {
    // Aceita string, URL ou Request - extrai sempre como string para verificar
    const urlStr = url instanceof URL ? url.href
                 : url instanceof Request ? url.url
                 : typeof url === 'string' ? url
                 : '';
    // Em desenvolvimento a API atende na 8000; em produção ela é servida
    // pelo PRÓPRIO endereço, sob /api. Sem reconhecer as duas formas, o
    // token não seria anexado em produção e TODA chamada voltaria 401 —
    // e a trava de troca de empresa (a dos R$ 680.000) ficaria desligada.
    const ehAPI = urlStr.includes(':8000')
               || urlStr.startsWith('/api/')
               || (typeof window !== 'undefined'
                   && urlStr.startsWith(`${window.location.origin}/api/`));
    if (ehAPI) {
      const alvo = empresaDaURL(urlStr);
      // Saida: a tela pediu dados de uma empresa que ja nao esta selecionada.
      if (alvo && window.__empresaEmFoco && alvo !== window.__empresaEmFoco) {
        return DESCARTADA();
      }
      const token = await obterToken();
      if (token) {
        options.headers = {
          ...(options.headers || {}),
          'Authorization': `Bearer ${token}`,
        };
      }
      // Aborta de verdade ao trocar de empresa, em vez de so ignorar a
      // resposta: a requisicao ignorada mantinha o socket ocupado ate o
      // servidor responder, e o Chrome so permite 6 conexoes simultaneas por
      // origem — algumas trocas em rajada esgotavam as 6 e a tela parava.
      // Abortada, a conexao volta para o pool na hora.
      const ctrl = new AbortController();
      const abortar = () => ctrl.abort();
      if (alvo) _emVoo.add({ alvo, abortar });
      let resposta;
      try {
        resposta = await fetchOriginal(url, { ...options, signal: ctrl.signal });
      } catch (e) {
        // Abortada por troca de empresa: descarta em silencio, como antes.
        // Qualquer outro erro segue para o `.catch` de quem chamou.
        if (e?.name === 'AbortError') return DESCARTADA();
        throw e;
      } finally {
        _emVoo.forEach(r => { if (r.abortar === abortar) _emVoo.delete(r); });
      }
      // Chegada: a empresa mudou enquanto esta requisicao estava em voo.
      // Este e o caso que produziu os R$ 680.000 da Construmax sob o nome MORED.
      // A trava continua aqui mesmo com o abort: a resposta pode ter chegado
      // no mesmo instante da troca, antes de o abort surtir efeito.
      if (alvo && window.__empresaEmFoco && alvo !== window.__empresaEmFoco) {
        return DESCARTADA();
      }
      return resposta;
    }
    return fetchOriginal(url, options);
  };
  window.__fetchInterceptorInstalled = true;
}

// Em produção o frontend é servido pelo PRÓPRIO backend e a API mora em
// /api do mesmo endereço — sem CORS, um endereço só (plano de 27/08,
// item 3.2). Em desenvolvimento o backend segue separado na 8000.
// ABSOLUTO de propósito: dez chamadas usam `new URL(`${API}/…`)`, e o
// construtor URL exige endereço absoluto — com '/api' relativo ele lança
// "Invalid URL" e a tela mostra erro. `window.location.origin` resolve para
// o próprio endereço servido, que é onde a API mora em produção.
const API = process.env.NODE_ENV === 'production'
  ? `${window.location.origin}/api`
  : 'http://127.0.0.1:8000/api';

// `focarEmpresa` e `guardarToken` já saem exportados acima, como no original.
export { API };
